import type { ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { useAuthStore, useTenantStore } from '@/stores'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SidebarSeparator,
} from '@/components/ui/sidebar'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Home,
  Send,
  Tag,
  History,
  LogOut,
  UserCog,
  Users,
  MessageSquare,
  Grid3x3,
  ExternalLink,
  BarChart3,
  ClipboardList,
  ListChecks,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Identicon } from '@/components/Identicon'

interface MainLayoutProps {
  children: ReactNode
}

const MainLayout = observer(({ children }: MainLayoutProps) => {
  const authStore = useAuthStore()
  const tenantStore = useTenantStore()
  const navigate = useNavigate()
  const location = useLocation()
  const tenantId = authStore.currentTenantId

  const hogeSeriesProducts = [
    {
      name: 'hogeAdmin',
      url: import.meta.env.VITE_HOGE_ADMIN_URL,
      icon: UserCog,
      description: 'テナント管理',
      current: false,
    },
    {
      name: 'hogeCast',
      url: import.meta.env.VITE_HOGE_CAST_URL,
      icon: MessageSquare,
      description: 'セグメント配信',
      current: true,
    },
    {
      name: 'hogeAgent',
      url: import.meta.env.VITE_HOGE_AGENT_URL,
      icon: Users,
      description: 'リアルタイムチャット',
      current: false,
    },
  ]

  // なりすましモード中は通常ユーザーのメニューを表示
  const isRootMode = authStore.isRootTenant && !authStore.isSpoofing
  
  const menuItems = [
    {
      label: 'ダッシュボード',
      icon: Home,
      path: '/',
      show: true,
    },
    {
      label: '通知送信',
      icon: Send,
      path: '/notifications/send',
      show: !isRootMode && tenantId,
    },
    {
      label: '配信一覧',
      icon: History,
      path: '/notifications/history',
      show: !isRootMode && tenantId,
    },
    {
      label: '統計・分析',
      icon: BarChart3,
      path: '/notifications/stats',
      show: !isRootMode && tenantId,
    },
    {
      label: 'アンケート',
      icon: ClipboardList,
      path: '/surveys',
      show: !isRootMode && tenantId,
    },
  ]

  const settingsItems = [
    {
      label: 'タグ管理',
      icon: Tag,
      path: '/notifications/tags',
      show: !isRootMode && tenantId,
    },
    {
      label: '配信対象設定',
      icon: ListChecks,
      path: '/delivery-targets',
      show: !isRootMode && tenantId,
    },
  ]

  const handleLogout = () => {
    // なりすまし中はログアウトできない
    if (authStore.isSpoofing) {
      return
    }
    authStore.logout()
    navigate('/auth/login')
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <Sidebar>
        <div className="flex items-center justify-center text-[28px] h-[53px] bg-[#3e8981]">
          {/* <WhiteLogo height={28} /> */}
          <span>hogeCast</span>
        </div>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>メニュー</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {menuItems.filter(item => item.show).map((item) => (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        isActive={location.pathname === item.path}
                        onClick={() => navigate(item.path)}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {settingsItems.some(item => item.show) && (
              <SidebarGroup>
                <SidebarGroupLabel>設定</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {settingsItems.filter(item => item.show).map((item) => (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          isActive={location.pathname === item.path}
                          onClick={() => navigate(item.path)}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}

            <div className="mt-auto">
              {authStore.isSpoofing && (
                <>
                  <SidebarSeparator />
                  <div className="bg-destructive text-destructive-foreground p-3">
                    <div className="text-sm text-white">
                      なりすまし管理中: {tenantStore.currentTenant?.name} ({authStore.currentTenantId})
                    </div>
                    <div className="text-xs text-white/80 mt-1">
                      ※ hogeAdminから終了してください
                    </div>
                  </div>
                </>
              )}
              
              <SidebarSeparator />
              
              <SidebarGroup>
                <SidebarGroupContent>
                  <div className="flex items-center gap-3 p-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback>
                        <Identicon source={authStore.user?.email || ''} size={40} />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {authStore.user?.name}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {authStore.user?.email}
                      </div>
                    </div>
                  </div>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton 
                        onClick={handleLogout}
                        disabled={authStore.isSpoofing}
                        className={authStore.isSpoofing ? "opacity-50 cursor-not-allowed" : ""}
                      >
                        <LogOut className="h-4 w-4" />
                        <span>{authStore.isSpoofing ? "ログアウト (無効)" : "ログアウト"}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </div>
          </SidebarContent>
        </Sidebar>

        <div className="flex-1 flex flex-col">
          <header className="border-b px-4 py-3 flex items-center justify-between">
            <SidebarTrigger />
            
            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Grid3x3 className="h-4 w-4" />
                    <span className="hidden sm:inline">hogeSeries</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>プロダクト一覧</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {hogeSeriesProducts.map((product) => (
                    <DropdownMenuItem
                      key={product.name}
                      disabled={product.current}
                      onClick={() => window.location.href = product.url}
                      className="cursor-pointer"
                    >
                      <product.icon className="mr-2 h-4 w-4" />
                      <div className="flex-1">
                        <div className="font-medium">{product.name}</div>
                        <div className="text-xs text-muted-foreground">{product.description}</div>
                      </div>
                      {!product.current && <ExternalLink className="ml-2 h-3 w-3" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <main className="flex-1 overflow-auto p-6">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  )
})

export default MainLayout